import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { SectionTitle, StatusPill } from "@/components/dashboard/widgets";
import { Bug, Lightbulb, ThumbsUp, MoreHorizontal, MailCheck, MessageSquareText, ExternalLink } from "lucide-react";

const CAT_ICON = { bug: Bug, idea: Lightbulb, compliment: ThumbsUp, other: MoreHorizontal };
const CAT_HUE = {
  bug: "bg-rose-50 text-rose-700 ring-rose-200",
  idea: "bg-amber-50 text-amber-700 ring-amber-200",
  compliment: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  other: "bg-slate-100 text-slate-700 ring-slate-200",
};

export default function MyFeedback() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/me/feedback")
      .then((r) => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6">
      <SectionTitle title="My feedback" subtitle="Everything you've sent us via the Feedback widget — with live status from our team." />

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && rows.length === 0 && (
        <Card className="rounded-xl shadow-soft border-slate-200">
          <CardContent className="p-10 text-center">
            <MessageSquareText className="size-8 text-slate-300 mx-auto" />
            <h3 className="mt-3 text-base font-semibold text-slate-900">No reports yet</h3>
            <p className="mt-1 text-sm text-slate-500 max-w-md mx-auto">
              Spot a bug, have an idea, or want to give us a compliment? Click the <strong>Feedback</strong> button in the bottom-right corner of any page.
            </p>
          </CardContent>
        </Card>
      )}

      {!loading && rows.length > 0 && (
        <ul className="space-y-3" data-testid="my-feedback-list">
          {rows.map((r) => {
            const Icon = CAT_ICON[r.category] || MoreHorizontal;
            const hue = CAT_HUE[r.category] || CAT_HUE.other;
            return (
              <li key={r.id} className="rounded-xl border border-slate-200 bg-white p-5 shadow-soft" data-testid={`my-fb-${r.id}`}>
                <div className="flex items-start gap-3">
                  <span className={`inline-grid place-items-center size-8 rounded-md ring-1 shrink-0 ${hue}`}>
                    <Icon className="size-4" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10.5px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ring-1 ${hue}`}>{r.category}</span>
                      <StatusPill status={r.status} />
                      {r.user_notified_at && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 font-medium" data-testid={`my-fb-notified-${r.id}`}>
                          <MailCheck className="size-3" /> Reply emailed
                        </span>
                      )}
                      <span className="text-[11px] text-slate-400 ml-auto font-mono tabular-nums">{new Date(r.created_at).toLocaleString("en-GB")}</span>
                    </div>
                    <p className="mt-2 text-sm text-slate-900 leading-relaxed whitespace-pre-wrap">{r.description}</p>

                    {r.reply_note && (
                      <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2.5">
                        <div className="text-[10.5px] uppercase tracking-widest text-primary font-semibold mb-1">Reply from Connexis</div>
                        <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{r.reply_note}</p>
                        {r.replied_at && <div className="mt-1.5 text-[10.5px] text-slate-500 font-mono">{new Date(r.replied_at).toLocaleString("en-GB")}</div>}
                      </div>
                    )}

                    {r.page_url && (
                      <div className="mt-2 text-[11px] text-slate-500 flex items-center gap-1.5">
                        <ExternalLink className="size-3" />
                        <span className="truncate">{(() => { try { return new URL(r.page_url).pathname; } catch { return r.page_url; } })()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
